import { formatPercent, formatTraceNumber } from "./priceTraceFormatters";
import { resolveOrganizedTourSupplierCommissionDisplay } from "./resolveOrganizedTourSupplierCommissionDisplay";
import { resolveOrganizedTourSearchCardPackageIls } from "../../../modules/organizedTours/utils/resolveOrganizedTourSearchCardPackageIls";
import { computeOrganizedTourAgentQuoteCommission } from "../../../modules/organizedTours/utils/computeOrganizedTourAgentQuoteCommission";

const NA = "לא זמין";

function num(v) {
  if (v == null || v === "") return null;
  if (typeof v === "object") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function rateFromMap(map, cur) {
  if (!map || typeof map !== "object" || Array.isArray(map)) return null;
  const key = String(cur || "").trim().toUpperCase();
  if (!key || key === "—") return null;
  return num(map[key]) ?? num(map[key.toLowerCase()]);
}

function impliedIlsPerSrc(ils, srcAmt) {
  const a = num(ils);
  const b = num(srcAmt);
  if (a == null || b == null || b === 0) return null;
  const r = a / b;
  return Number.isFinite(r) && r > 0 ? r : null;
}

function ratesClose(a, b) {
  if (a == null || b == null || Number(b) === 0) return false;
  return Math.abs(Number(a) - Number(b)) / Math.abs(Number(b)) < 0.025;
}

/**
 * FX used on TRACE: per-currency snapshot first, never a leftover USD admin 4
 * when ILS was clearly converted at another rate.
 */
export function pickOrganizedTourTraceFxRate({
  otf = {},
  snap = null,
  srcCurrency = "",
  ilsAmount = null,
  srcAmount = null,
} = {}) {
  const cur = String(srcCurrency || "").trim().toUpperCase();
  const implied = impliedIlsPerSrc(ilsAmount, srcAmount);
  const scalarRate = typeof otf.currencyRate === "object" ? null : num(otf.currencyRate);
  const candidates = [
    { rate: num(snap?.fx_rate_to_ils), source: "pricingSnapshot.fx_rate_to_ils" },
    { rate: rateFromMap(snap?.exchange_rates_used, cur), source: `exchange_rates_used.${cur}` },
    { rate: rateFromMap(otf.currencyRate, cur), source: `ourTotalFare.currencyRate.${cur}` },
    { rate: scalarRate, source: "ourTotalFare.currencyRate" },
    { rate: num(snap?.fx_rate), source: "pricingSnapshot.fx_rate" },
  ].filter((c) => c.rate != null && c.rate > 0);

  if (implied != null) {
    const matched = candidates.find((c) => ratesClose(c.rate, implied));
    if (matched) return { rate: matched.rate, source: matched.source };
    return { rate: implied, source: "initialPrice ÷ נטו ספק" };
  }
  return candidates[0] || { rate: null, source: "" };
}

function fmtSrc(n, currency) {
  if (n == null || !Number.isFinite(Number(n))) return NA;
  const cur = String(currency || "").trim().toUpperCase();
  const amt = formatTraceNumber(Number(n));
  return cur && cur !== "—" ? `${amt} ${cur}` : amt;
}

function fmtIls(n) {
  if (n == null || !Number.isFinite(Number(n))) return NA;
  return `${Math.round(Number(n)).toLocaleString("he-IL")} ₪`;
}

function fmtIlsAndSrc(ils, srcAmt, currency) {
  const ilsPart = ils != null && Number.isFinite(Number(ils)) ? fmtIls(ils) : null;
  const srcPart =
    srcAmt != null && Number.isFinite(Number(srcAmt)) ? fmtSrc(srcAmt, currency) : null;
  if (ilsPart && srcPart) return `${ilsPart} · ${srcPart}`;
  return ilsPart || srcPart || NA;
}

function pickOrigCurrencyFare(otf, ourPrice) {
  const a = otf?.origCurrency;
  if (a && typeof a === "object") return a;
  const b = ourPrice?.origCurrency;
  if (b && typeof b === "object") return b;
  return null;
}

function pickOurPrice(deal) {
  const root = deal && typeof deal === "object" ? deal : {};
  return (
    root.ourPrice ||
    root.fullDeal?.ourPrice ||
    (root.price && typeof root.price === "object" ? root.price.ourPrice : null) ||
    null
  );
}

/**
 * Matched lds_suppliers / admin-table row already selected by pricing SSOT.
 */
export function pickOrganizedTourSupplierRule(deal, { ourPrice, snap, hrTrace } = {}) {
  const root = deal && typeof deal === "object" ? deal : {};
  const our = ourPrice || pickOurPrice(root);
  const candidates = [
    snap?.supplier_rule,
    our?.ldsCommissionCondition,
    root.ldsCommissionCondition,
    root.fullDeal?.ldsCommissionCondition,
    hrTrace?.selected_supplier_rule,
    hrTrace?.supplier_hr_margin_override_audit?.selected_supplier_rule,
  ];
  for (const c of candidates) {
    if (c && typeof c === "object") return c;
  }
  return null;
}

export function pickOrganizedTourCommissionablePax(deal, { snap, otf, hrTrace } = {}) {
  const fromSnap = num(snap?.commissionable_pax_count);
  if (fromSnap != null && fromSnap > 0) return fromSnap;
  const fromOtf = num(otf?.organizedTourHrPassengerCountForMargin);
  if (fromOtf != null && fromOtf > 0) return fromOtf;
  const fromHr = num(hrTrace?.passenger_count_used);
  if (fromHr != null && fromHr > 0) return fromHr;

  const root = deal && typeof deal === "object" ? deal : {};
  const rooms =
    root.passengersInfo ||
    root.passengers_info ||
    root.roomsAndPaxInfo ||
    root.fullDeal?.passengersInfo ||
    root.fullDeal?.passengers_info ||
    null;
  if (!Array.isArray(rooms) || rooms.length === 0) return null;
  let n = 0;
  for (const room of rooms) {
    if (!room || typeof room !== "object") continue;
    const adults = Array.isArray(room.adults)
      ? room.adults.length
      : Number(room.adults) || 0;
    const kids = Array.isArray(room.kids)
      ? room.kids.length
      : Array.isArray(room.children)
        ? room.children.length
        : Number(room.kids ?? room.children) || 0;
    n += adults + kids;
  }
  return n > 0 ? n : null;
}

function formatRuleMatchHint(rule) {
  if (!rule || typeof rule !== "object") return "";
  const parts = [
    rule.supplier_code != null ? `קוד ${rule.supplier_code}` : null,
    rule.supplier_name != null ? String(rule.supplier_name) : null,
    rule.lds_type != null ? String(rule.lds_type) : null,
    rule.destination != null ? String(rule.destination) : null,
  ].filter(Boolean);
  if (!parts.length) return "טבלת אדמין (lds_suppliers)";
  return `טבלת אדמין (lds_suppliers) · ${parts.join(" · ")}`;
}

/**
 * Primary TRACE lines for admin card/sidebar. Reads existing SSOT only.
 *
 * @param {{
 *   deal: object|null|undefined,
 *   displayIls?: number|null,
 *   calculateLDSDealFareForRoomsComposition?: Function|null,
 * }} p
 */
export function resolveOrganizedTourPrimaryPriceTrace(p = {}) {
  const { deal, displayIls = null, calculateLDSDealFareForRoomsComposition = null } = p;
  const root = deal && typeof deal === "object" ? deal : {};
  const ourPrice = pickOurPrice(root);
  const otf =
    ourPrice?.ourTotalFare && typeof ourPrice.ourTotalFare === "object"
      ? ourPrice.ourTotalFare
      : {};
  const snap =
    ourPrice?.pricingSnapshot && typeof ourPrice.pricingSnapshot === "object"
      ? ourPrice.pricingSnapshot
      : null;
  const hrTrace =
    snap?.hr_margin_resolver_trace && typeof snap.hr_margin_resolver_trace === "object"
      ? snap.hr_margin_resolver_trace
      : null;
  const dealFare = root.dealFare || root.fullDeal?.dealFare || {};
  const fareDetails = root.fareDetails || root.fullDeal?.fareDetails || {};

  const srcCurrencyRaw =
    otf.fx_currency ||
    otf.originalCurrency ||
    fareDetails.currency ||
    snap?.currency ||
    snap?.source_currency ||
    null;
  const srcCurrency = String(srcCurrencyRaw || "").trim().toUpperCase() || "—";

  const apiGross =
    num(snap?.supplier_price_original) ??
    num(snap?.supplier_gross_source) ??
    num(otf.supplierGrossSource) ??
    num(otf.ldsPrice) ??
    num(dealFare.totalFare);

  const adultFare = num(dealFare.adultFare);
  const pax = pickOrganizedTourCommissionablePax(root, { snap, otf, hrTrace });

  let apiUnit = "לחבילה";
  let apiPerPerson = null;
  if (pax === 1) {
    apiUnit = "לאדם";
  } else if (pax != null && pax > 1 && apiGross != null) {
    apiPerPerson = apiGross / pax;
  } else if (adultFare != null && apiGross != null && Math.abs(adultFare - apiGross) < 0.51) {
    apiUnit = "לאדם";
  }

  const supplierNet =
    num(snap?.supplier_net_price) ??
    num(snap?.supplier_net) ??
    num(otf.supplierNetSource) ??
    num(otf.netPrice);

  const rule = pickOrganizedTourSupplierRule(root, { ourPrice, snap, hrTrace });
  const snapForCommission =
    snap || rule
      ? { ...(snap && typeof snap === "object" ? snap : {}), supplier_rule: snap?.supplier_rule || rule }
      : null;

  const supplierCommission = resolveOrganizedTourSupplierCommissionDisplay({
    snap: snapForCommission,
    fareDetails,
    gross: apiGross,
    net: supplierNet,
  });

  const configuredPerPerson =
    num(snap?.supplier_commission_per_pax_source) ??
    num(snap?.configured_fixed_commission_per_person) ??
    num(rule?.fixed_commission_per_person);

  const commissionPctOnly = num(rule?.commission_percentage);
  const discountPct = num(rule?.discount_percentage);

  let supplierCommissionPerPerson = configuredPerPerson;
  let supplierCommissionPerPersonSource = configuredPerPerson != null
    ? "lds_suppliers.fixed_commission_per_person"
    : "";

  if (supplierCommissionPerPerson == null && pax != null && pax > 0) {
    if (commissionPctOnly != null && apiGross != null) {
      supplierCommissionPerPerson = (apiGross * commissionPctOnly) / 100 / pax;
      supplierCommissionPerPersonSource = "commission_percentage ÷ נוסעים";
    } else if (supplierCommission.deduction != null) {
      const discountAmt =
        discountPct != null && apiGross != null ? (apiGross * discountPct) / 100 : 0;
      const commissionOnly = Number(supplierCommission.deduction) - Number(discountAmt || 0);
      if (Number.isFinite(commissionOnly)) {
        supplierCommissionPerPerson = commissionOnly / pax;
        supplierCommissionPerPersonSource = "ניכוי ספק ÷ נוסעים";
      }
    }
  }

  let supplierCommissionDisplay = NA;
  let supplierCommissionHint = supplierCommission.supplier_commission_type_he || "";
  if (supplierCommissionPerPerson != null) {
    supplierCommissionDisplay = fmtSrc(supplierCommissionPerPerson, srcCurrency);
    if (supplierCommission.supplier_commission_value_display) {
      supplierCommissionHint = [
        supplierCommission.supplier_commission_value_display,
        supplierCommission.supplier_commission_type_he,
        pax != null ? `${pax} נוסעים` : null,
      ]
        .filter(Boolean)
        .join(" · ");
    }
  } else if (supplierCommission.supplier_commission_value_display) {
    supplierCommissionDisplay = String(supplierCommission.supplier_commission_value_display);
    supplierCommissionHint = `${supplierCommission.supplier_commission_type_he || ""} · סכום לאדם לא זמין`.trim();
  } else if (supplierCommission.supplier_commission_amount_source_currency_display) {
    supplierCommissionDisplay = String(
      supplierCommission.supplier_commission_amount_source_currency_display,
    );
    supplierCommissionHint = "סה״כ לחבילה (אין פילוח לאדם)";
  }

  let supplierCommissionTotal = null;
  let supplierCommissionTotalSource = "";
  if (supplierCommissionPerPerson != null && pax != null && pax > 0) {
    supplierCommissionTotal = supplierCommissionPerPerson * pax;
    supplierCommissionTotalSource = "עמלת ספק לאדם × נוסעים";
  } else if (supplierCommission.deduction != null) {
    const discountAmtOnly =
      discountPct != null && apiGross != null ? (apiGross * discountPct) / 100 : null;
    if (discountAmtOnly != null) {
      supplierCommissionTotal = Number(supplierCommission.deduction) - Number(discountAmtOnly);
      supplierCommissionTotalSource = "ניכוי ספק − הנחת אדמין";
    } else {
      supplierCommissionTotal = Number(supplierCommission.deduction);
      supplierCommissionTotalSource = "supplier_deduction / gross−net";
    }
  }
  if (supplierCommissionTotal != null && !Number.isFinite(supplierCommissionTotal)) {
    supplierCommissionTotal = null;
  }

  const supplierCommissionTotalHint = [
    commissionPctOnly != null ? formatPercent(commissionPctOnly) : null,
    pax != null ? `${pax} נוסעים` : null,
    supplierCommissionTotalSource || null,
  ]
    .filter(Boolean)
    .join(" · ");

  const discountAmountSrc =
    discountPct != null && apiGross != null ? (apiGross * discountPct) / 100 : null;
  let adminDiscountDisplay = NA;
  if (discountPct != null) {
    const pctLabel = formatPercent(discountPct);
    adminDiscountDisplay =
      discountAmountSrc != null ? `${pctLabel} · ${fmtSrc(discountAmountSrc, srcCurrency)}` : pctLabel;
  }
  const adminDiscountHint = formatRuleMatchHint(rule);

  const computedDisplayIls =
    displayIls != null && Number.isFinite(Number(displayIls))
      ? Number(displayIls)
      : resolveOrganizedTourSearchCardPackageIls({
          root,
          raw: root,
          deal: root,
          calculateLDSDealFareForRoomsComposition,
        });

  const quote = computeOrganizedTourAgentQuoteCommission({
    selectedResult: root,
    baselineCheckoutIls: computedDisplayIls,
    override: null,
  });

  const apiHintParts = [apiUnit];
  if (apiPerPerson != null) {
    apiHintParts.push(`${fmtSrc(apiPerPerson, srcCurrency)} לאדם`);
  }
  if (pax != null && pax > 1) {
    apiHintParts.push(`${pax} נוסעים`);
  }

  const initialPrice = num(otf.initialPrice);
  let { rate: fxRate, source: fxRateSource } = pickOrganizedTourTraceFxRate({
    otf,
    snap,
    srcCurrency,
    ilsAmount: initialPrice,
    srcAmount: supplierNet,
  });
  const hrPct =
    num(hrTrace?.final_hr_margin_percent) ??
    num(snap?.hr_margin_percent) ??
    num(otf.hrCommissionPercent) ??
    num(otf.unifiedHrMarginPercent);
  const hrAmountIls =
    num(hrTrace?.calculated_hr_margin_ils) ??
    num(otf.hrCommission) ??
    num(snap?.hr_markup_line_item_ils);
  const handling = num(otf.handlingFee) ?? num(ourPrice?.handlingFee) ?? num(ourPrice?.handleFee);

  const supplierNetIlsFallback =
    supplierNet != null && fxRate != null && fxRate > 0
      ? Math.ceil(supplierNet * fxRate)
      : null;
  const beforeHrIls = initialPrice ?? supplierNetIlsFallback;
  const beforeHrSource =
    initialPrice != null
      ? "ourTotalFare.initialPrice"
      : supplierNetIlsFallback != null
        ? "supplier_net × FX"
        : "";
  const afterHrIls = num(otf.priceWithCommission);

  const origFare = pickOrigCurrencyFare(otf, ourPrice);
  let beforeHrSrc = supplierNet;
  let beforeHrSrcSource = supplierNet != null ? "supplier_net_price / supplierNetSource / netPrice" : "";
  if (beforeHrSrc == null && apiGross != null && supplierCommissionTotal != null) {
    beforeHrSrc = apiGross - supplierCommissionTotal;
    beforeHrSrcSource = "API gross − סה״כ עמלה";
  }
  if (beforeHrSrc == null && beforeHrIls != null && fxRate != null && fxRate > 0) {
    beforeHrSrc = beforeHrIls / fxRate;
    beforeHrSrcSource = "initialPrice ÷ FX";
  }

  let afterHrSrc =
    num(origFare?.priceWithCommission) ??
    num(otf.priceWithCommissionSource) ??
    num(otf.priceWithCommissionOriginal);
  let afterHrSrcSource =
    afterHrSrc != null
      ? origFare?.priceWithCommission != null
        ? "origCurrency.priceWithCommission"
        : "ourTotalFare.priceWithCommissionSource"
      : "";
  if (afterHrSrc == null && srcCurrency === "USD") {
    afterHrSrc =
      num(snap?.priceWithCommissionUSD) ??
      num(otf.priceWithCommissionUSD) ??
      num(ourPrice?.priceWithCommissionUSD);
    if (afterHrSrc != null) afterHrSrcSource = "priceWithCommissionUSD";
  }
  if (afterHrSrc == null && afterHrIls != null && fxRate != null && fxRate > 0) {
    afterHrSrc = afterHrIls / fxRate;
    afterHrSrcSource = "priceWithCommission ÷ FX";
  }

  const afterHrSrcFellWhileIlsRose =
    beforeHrSrc != null &&
    afterHrSrc != null &&
    beforeHrIls != null &&
    afterHrIls != null &&
    afterHrIls + 0.5 >= beforeHrIls &&
    afterHrSrc < beforeHrSrc - 0.5;
  if (afterHrSrcFellWhileIlsRose) {
    const implied = impliedIlsPerSrc(beforeHrIls, beforeHrSrc);
    if (implied != null && afterHrIls != null) {
      fxRate = implied;
      fxRateSource = "initialPrice ÷ נטו ספק";
      afterHrSrc = afterHrIls / implied;
      afterHrSrcSource = "priceWithCommission ÷ (initialPrice ÷ נטו ספק)";
    }
  }

  /**
   * נטו לסוכן = אחרי HR (priceWithCommission).
   * Card displayIls is the same SSOT after sanitize; ignore it when it still
   * equals pre-HR initialPrice.
   */
  const displayLooksPreHr =
    computedDisplayIls != null &&
    beforeHrIls != null &&
    Math.abs(Number(computedDisplayIls) - Number(beforeHrIls)) <= 1.5;

  let agentNetIls = afterHrIls;
  if (agentNetIls == null && Number.isFinite(quote.basePrice)) {
    agentNetIls = quote.basePrice;
  }
  if (agentNetIls == null && computedDisplayIls != null && !displayLooksPreHr) {
    agentNetIls = computedDisplayIls;
  }

  const hrHintParts = [
    hrPct != null ? `עמלת HR ${formatPercent(hrPct)}` : null,
    hrAmountIls != null ? fmtIls(hrAmountIls) : null,
  ].filter(Boolean);

  const primaryRows = [
    {
      key: "api",
      label: "מחיר API",
      value: apiGross != null ? fmtSrc(apiGross, srcCurrency) : NA,
      hint: apiHintParts.filter(Boolean).join(" · "),
      unit: apiUnit,
      raw: apiGross,
    },
    {
      key: "supplier_commission_per_person",
      label: "עמלת ספק לאדם",
      value: supplierCommissionDisplay,
      hint: supplierCommissionHint,
      unit: "לאדם",
      raw: supplierCommissionPerPerson,
    },
    {
      key: "supplier_commission_total",
      label: "סה״כ עמלה",
      value: supplierCommissionTotal != null ? fmtSrc(supplierCommissionTotal, srcCurrency) : NA,
      hint: supplierCommissionTotalHint,
      unit: "לחבילה",
      raw: supplierCommissionTotal,
    },
    {
      key: "admin_discount",
      label: "הנחת אדמין",
      value: adminDiscountDisplay,
      hint: adminDiscountHint,
      unit: discountPct != null ? "%" : "",
      raw: discountPct,
    },
    {
      key: "before_hr",
      label: "לפני עמלת HR",
      value: fmtIlsAndSrc(beforeHrIls, beforeHrSrc, srcCurrency),
      hint: ["לחבילה", beforeHrSource, beforeHrSrcSource, ...hrHintParts].filter(Boolean).join(" · "),
      unit: "לחבילה",
      raw: beforeHrIls,
    },
    {
      key: "after_hr",
      label: "אחרי עמלת HR",
      value: fmtIlsAndSrc(afterHrIls ?? agentNetIls, afterHrSrc, srcCurrency),
      hint: ["לחבילה · נטו לסוכן סופי", afterHrSrcSource, ...hrHintParts].filter(Boolean).join(" · "),
      unit: "לחבילה",
      raw: afterHrIls ?? agentNetIls,
    },
  ];

  const netsDifferFromAfter =
    agentNetIls != null &&
    afterHrIls != null &&
    Math.abs(Number(agentNetIls) - Number(afterHrIls)) > 1.5;
  const netsCopyBeforeHr =
    agentNetIls != null &&
    beforeHrIls != null &&
    Math.abs(Number(agentNetIls) - Number(beforeHrIls)) <= 1.5;
  if (netsDifferFromAfter && !netsCopyBeforeHr) {
    primaryRows.push({
      key: "agent_net",
      label: "נטו לסוכן סופי",
      value: fmtIls(agentNetIls),
      hint: "לחבילה · אחרי עמלת HR",
      unit: "לחבילה",
      raw: agentNetIls,
    });
  }

  const extraRows = [];
  if (fxRate != null) {
    extraRows.push({
      key: "fx",
      label: "שער FX",
      value: formatTraceNumber(fxRate),
      hint: [srcCurrency !== "—" ? `${srcCurrency} → ILS` : null, fxRateSource || null]
        .filter(Boolean)
        .join(" · "),
    });
  }
  if (handling != null && handling !== 0) {
    extraRows.push({
      key: "handling",
      label: "דמי טיפול",
      value: fmtIls(handling),
      hint: "",
    });
  }

  return {
    apiGross,
    apiUnit,
    apiPerPerson,
    srcCurrency,
    pax,
    supplierCommission,
    supplierCommissionPerPerson,
    supplierCommissionPerPersonSource,
    supplierCommissionTotal,
    supplierCommissionTotalSource,
    beforeHrSrc,
    beforeHrSrcSource,
    afterHrSrc,
    afterHrSrcSource,
    discountPct,
    discountAmountSrc,
    rule,
    ruleMatchHint: adminDiscountHint,
    beforeHrIls,
    afterHrIls,
    beforeHrSource,
    fxRate,
    fxRateSource,
    agentNetIls,
    primaryRows,
    extraRows,
  };
}
